import { useParams, useNavigate } from 'react-router-dom';
import {
  useAnalysis,
  useCreateManualMeasure,
  useUpdateManualMeasure,
  useDeleteManualMeasure,
  useUpdateWireframeUrl,
  useUploadAnalysisAsset,
  useDeleteAnalysisAsset,
  useUpdateAnalysisAsset,
  useUpdateFoundations,
  useUpdateTopMeasures,
} from '../hooks/useAnalysis';
import api from '../lib/axios';
import { useSaveReport } from '../hooks/useReports';
import Badge from '../components/ui/Badge';
import ScorePill from '../components/ui/ScorePill';
import Bar from '../components/ui/Bar';
import { useState, Fragment } from 'react';

const PRIORITY_MAP = {
  high: { label: 'Hoch', bg: '#FEE2E2', color: '#EF4444', icon: '🔴' },
  medium: { label: 'Mittel', bg: '#FEF3C7', color: '#F59E0B', icon: '🟠' },
  low: { label: 'Gering', bg: '#D1FAE5', color: '#10B981', icon: '🟡' },
};

const FOUNDATION_TEMPLATE = {
  headline: 'Grundlagen — Wahrnehmung & Vertrauen',
  intro: '',
  blocks: [
    { key: 'z_pattern', title: 'Z-Pattern', body: '', applies: true },
    { key: 'gutenberg', title: 'Gutenberg-Diagramm', body: '', applies: false },
    { key: 'f_pattern', title: 'F-Pattern', body: '', applies: false },
    { key: 'trust', title: 'Vertrauensaufbau (Social Proof, Reziprozität, Risiko-Reduktion)', body: '', applies: true },
    { key: 'cognitive_ease', title: 'Kognitive Leichtigkeit als Leitprinzip', body: '', applies: true },
  ],
};

const CATEGORY_MAP = {
  copy: { label: 'Copy', bg: '#E0F2FE', color: '#0369A1' },
  cta: { label: 'CTA', bg: '#FEF3C7', color: '#B45309' },
  layout: { label: 'Layout', bg: '#EDE9FE', color: '#6D28D9' },
  psychology: { label: 'Psychologie', bg: '#FCE7F3', color: '#BE185D' },
  trust: { label: 'Trust', bg: '#DCFCE7', color: '#15803D' },
  performance: { label: 'Performance', bg: '#FFE4E6', color: '#BE123C' },
  mobile: { label: 'Mobile', bg: '#E0E7FF', color: '#4338CA' },
  analytics: { label: 'Analytics', bg: '#F1F5F9', color: '#475569' },
  accessibility: { label: 'A11y', bg: '#FEF2F2', color: '#9F1239' },
  seo: { label: 'SEO', bg: '#FFFBEB', color: '#A16207' },
  form: { label: 'Formular', bg: '#ECFEFF', color: '#0E7490' },
  checkout: { label: 'Checkout', bg: '#F0FDF4', color: '#166534' },
  personalization: { label: 'Personalisierung', bg: '#FAE8FF', color: '#86198F' },
  pricing: { label: 'Pricing', bg: '#FEF9C3', color: '#854D0E' },
  search: { label: 'Suche', bg: '#E0F2FE', color: '#075985' },
  internationalization: { label: 'i18n', bg: '#F3F4F6', color: '#374151' },
  'exit-intent': { label: 'Exit-Intent', bg: '#FFF1F2', color: '#9F1239' },
  other: { label: 'Sonstiges', bg: '#F3F4F6', color: '#4B5563' },
};

function ScreenshotZone({ title, description, accent, assets, fileUrl, onUpload, onDelete, onUpdateLabel, busy }) {
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingLabel, setPendingLabel] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState('');

  const submit = async () => {
    if (!pendingFile) return;
    const label = pendingLabel.trim() || pendingFile.name.replace(/\.[^.]+$/, '');
    await onUpload(pendingFile, label);
    setPendingFile(null);
    setPendingLabel('');
  };

  const accentBorder = accent === 'amber' ? 'border-l-[#F59E0B]' : 'border-l-teal';

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-black/[.07] border-l-4 ${accentBorder} p-6`}>
      <div className="mb-4">
        <h3 className="font-bold text-[16px] mb-1">{title}</h3>
        <p className="text-[12.5px] text-gray-500">{description}</p>
      </div>

      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setPendingFile(e.target.files?.[0] || null)}
            className="text-[12px]"
          />
          <input
            type="text"
            placeholder='Label, z.B. "Homepage ATF"'
            value={pendingLabel}
            onChange={(e) => setPendingLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && pendingFile && !busy) submit(); }}
            className="flex-1 min-w-[160px] px-3 py-1.5 text-[13px] border border-gray-300 rounded-lg focus:outline-none focus:border-teal"
            disabled={!pendingFile}
          />
          <button
            onClick={submit}
            disabled={!pendingFile || busy}
            className="px-4 py-1.5 bg-teal text-white rounded-lg text-[13px] font-medium hover:opacity-90 disabled:opacity-40 whitespace-nowrap"
          >
            {busy ? 'Lade…' : 'Hochladen'}
          </button>
        </div>
        <p className="text-[11px] text-gray-400 mt-2">
          {pendingFile
            ? `Bereit: ${pendingFile.name}` + (pendingLabel.trim() ? '' : ' — ohne Label wird der Dateiname verwendet')
            : 'PNG/JPG/WebP, max. 20 MB. Mehrere Uploads nacheinander, jeweils mit Label.'}
        </p>
      </div>

      {assets.length === 0 ? (
        <p className="text-[12.5px] text-gray-400 italic">Noch keine Screenshots in dieser Sektion.</p>
      ) : (
        <div className="grid grid-cols-3 gap-3">
          {assets.map((a) => (
            <div key={a.id} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
              <a href={fileUrl(a.id)} target="_blank" rel="noopener noreferrer" className="block bg-gray-50 aspect-[4/3] overflow-hidden">
                <img src={fileUrl(a.id)} alt={a.caption || ''} className="w-full h-full object-cover" />
              </a>
              <div className="p-2.5">
                {editingId === a.id ? (
                  <div className="flex items-center gap-1">
                    <input
                      type="text"
                      value={editLabel}
                      onChange={(e) => setEditLabel(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { onUpdateLabel(a.id, editLabel.trim() || null); setEditingId(null); }
                        if (e.key === 'Escape') setEditingId(null);
                      }}
                      className="flex-1 min-w-0 px-2 py-1 text-[12px] border border-gray-300 rounded"
                      autoFocus
                    />
                    <button
                      onClick={() => { onUpdateLabel(a.id, editLabel.trim() || null); setEditingId(null); }}
                      className="text-teal hover:underline text-[11px]"
                    >ok</button>
                  </div>
                ) : (
                  <div
                    onClick={() => { setEditingId(a.id); setEditLabel(a.caption || ''); }}
                    className="text-[12px] text-gray-700 truncate cursor-pointer hover:text-teal"
                    title="Klick zum Umbenennen"
                  >
                    {a.caption || <span className="text-gray-400 italic">Klick für Label</span>}
                  </div>
                )}
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-[10.5px] text-gray-400">{Math.round((a.size_bytes || 0) / 1024)} KB</span>
                  <button
                    onClick={() => onDelete(a.id)}
                    className="text-[10.5px] text-red-500 hover:underline"
                  >löschen</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AnalysisReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: analysis, isLoading } = useAnalysis(id);
  const saveReport = useSaveReport();
  const createManual = useCreateManualMeasure();
  const updateManual = useUpdateManualMeasure();
  const deleteManual = useDeleteManualMeasure();
  const updateWireframeUrl = useUpdateWireframeUrl();
  const uploadAsset = useUploadAnalysisAsset();
  const deleteAsset = useDeleteAnalysisAsset();
  const updateAsset = useUpdateAnalysisAsset();
  const updateFoundations = useUpdateFoundations();
  const updateTopMeasures = useUpdateTopMeasures();
  const [tab, setTab] = useState('summary');
  const [openMeasure, setOpenMeasure] = useState(null);
  const [manualForm, setManualForm] = useState(null); // null | 'new' | nr (=editing)
  const [formData, setFormData] = useState({});
  const [wireframeUrlDraft, setWireframeUrlDraft] = useState(null); // null = synced w/ analysis
  const [foundationsDraft, setFoundationsDraft] = useState(null);   // null = synced w/ analysis

  const handleCreateReport = async () => {
    try {
      const report = await saveReport.mutateAsync({ analysis_id: parseInt(id) });
      navigate('/reports');
    } catch (err) {
      alert('Fehler: ' + (err.response?.data?.message || err.message));
    }
  };

  if (isLoading || !analysis) {
    return <div className="flex items-center justify-center h-64 text-gray-400">Wird geladen…</div>;
  }

  const r = analysis.results || {};
  const narrative = r.narrative || {};
  const summary = r.management_summary;
  const webAnalytics = r.web_analytics;
  const userAnalytics = r.user_analytics || [];
  const measures = r.measures || [];
  const manualMeasures = analysis.manual_measures || [];
  const wireframes = r.wireframe_suggestions || [];
  const factors = r.factors || [];
  const foundations = analysis.foundations;
  const topNrs = analysis.top_measure_nrs || [];
  const topMeasures = manualMeasures.filter((m) => topNrs.includes(m.nr));
  const assets = analysis.assets || [];
  const assetsBySection = (section, measureNr = null) => assets.filter((a) =>
    a.type === 'screenshot'
    && (a.mime || '').startsWith('image/')
    && a.section === section
    && (measureNr == null || a.measure_nr === measureNr)
  );

  const fileUrl = (assetId) => `${api.defaults.baseURL || ''}/analyses/${id}/assets/${assetId}/file`;

  const handleSectionUpload = async (file, section, measureNr = null) => {
    if (!file) return;
    try {
      await uploadAsset.mutateAsync({
        analysisId: parseInt(id), file, type: 'screenshot',
        section, measure_nr: measureNr,
        caption: section + (measureNr ? ` · M${measureNr}` : ''),
      });
    } catch (err) {
      alert('Upload fehlgeschlagen: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleAssetDelete = async (assetId) => {
    if (!confirm('Bild wirklich löschen?')) return;
    try { await deleteAsset.mutateAsync({ analysisId: parseInt(id), assetId }); }
    catch (err) { alert('Fehler: ' + (err.response?.data?.message || err.message)); }
  };

  const toggleTopMeasure = async (nr) => {
    const next = topNrs.includes(nr)
      ? topNrs.filter((x) => x !== nr)
      : (topNrs.length >= 5 ? topNrs : [...topNrs, nr]);
    if (!topNrs.includes(nr) && topNrs.length >= 5) {
      alert('Maximal 5 Maßnahmen können als Top-Detail markiert werden. Erst eine entfernen.');
      return;
    }
    try { await updateTopMeasures.mutateAsync({ analysisId: parseInt(id), top_measure_nrs: next }); }
    catch (err) { alert('Fehler: ' + (err.response?.data?.message || err.message)); }
  };

  const promoteAiToTop = async (m) => {
    if (topNrs.length >= 5) { alert('Bereits 5 Top-Maßnahmen markiert.'); return; }
    try {
      const created = await createManual.mutateAsync({
        analysisId: parseInt(id),
        payload: {
          measure: m.measure, goal_effect: m.goal_effect || '', details: m.details || '',
          priority: m.priority || 'medium', effort: m.effort || 'medium',
          conv_impact: m.conv_impact || '', category: m.category || '', element_ref: m.element_ref || '',
        },
      });
      await updateTopMeasures.mutateAsync({
        analysisId: parseInt(id),
        top_measure_nrs: [...topNrs, created.nr],
      });
      startEditMeasure({ ...created });
    } catch (err) { alert('Fehler: ' + (err.response?.data?.message || err.message)); }
  };

  const emptyForm = {
    measure: '', goal_effect: '', details: '',
    priority: 'medium', effort: 'medium', conv_impact: '',
    category: '', element_ref: '',
    problem: '', hypothesis: '', foundation_ref: '', implementation: '', kpi: '', risk: '',
  };

  const startNewMeasure = () => { setFormData(emptyForm); setManualForm('new'); };
  const startEditMeasure = (m) => {
    setFormData({
      measure: m.measure || '', goal_effect: m.goal_effect || '', details: m.details || '',
      priority: m.priority || 'medium', effort: m.effort || 'medium',
      conv_impact: m.conv_impact || '', category: m.category || '', element_ref: m.element_ref || '',
      problem: m.problem || '', hypothesis: m.hypothesis || '',
      foundation_ref: m.foundation_ref || '', implementation: m.implementation || '',
      kpi: m.kpi || '', risk: m.risk || '',
    });
    setManualForm(m.nr);
  };
  const cancelForm = () => { setManualForm(null); setFormData({}); };
  const submitForm = async (e) => {
    e.preventDefault();
    const payload = { ...formData };
    if (!payload.measure?.trim()) return;
    Object.keys(payload).forEach(k => { if (payload[k] === '') delete payload[k]; });
    try {
      if (manualForm === 'new') {
        await createManual.mutateAsync({ analysisId: parseInt(id), payload });
      } else {
        await updateManual.mutateAsync({ analysisId: parseInt(id), nr: manualForm, payload });
      }
      cancelForm();
    } catch (err) {
      alert('Fehler: ' + (err.response?.data?.message || err.message));
    }
  };
  const handleDeleteManual = async (nr) => {
    if (!confirm('Diese Maßnahme wirklich löschen?')) return;
    try {
      await deleteManual.mutateAsync({ analysisId: parseInt(id), nr });
    } catch (err) {
      alert('Fehler: ' + (err.response?.data?.message || err.message));
    }
  };

  const TABS = [
    { key: 'summary', label: 'Management Summary' },
    { key: 'foundations', label: 'Grundlagen' },
    { key: 'web', label: 'Web Analytics' },
    { key: 'user', label: 'User Analytics' },
    { key: 'measures', label: 'Maßnahmen' + (topNrs.length > 0 ? ` · Top ${topNrs.length}/10` : '') },
    { key: 'wireframe', label: 'Screenshots' },
    ...(factors.length > 0 ? [{ key: 'factors', label: `Faktoren (${factors.length})` }] : []),
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-5">
        <div>
          <button onClick={() => navigate('/cro/analyses')} className="text-[12px] text-gray-500 hover:text-gray-700 mb-1.5 block">← Zurück zu Analysen</button>
          <h1 className="text-[23px] font-bold mb-0.5">Conversion Analyse</h1>
          <p className="text-gray-500 text-[13.5px]">
            {analysis.url}
            {analysis.contact?.name && <span> · {analysis.contact.name}</span>}
            {analysis.contact?.company && <span className="text-gray-400"> ({analysis.contact.company})</span>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <ScorePill score={analysis.score || 0} />
          <Badge bg="#F3F4F6" color="#6B7280">{analysis.provider || '—'}</Badge>
          <Badge bg="#E6F4F2" color="#0C9488">{analysis.page_type}</Badge>
          <button
            onClick={handleCreateReport}
            disabled={saveReport.isPending}
            className="ml-2 px-4 py-2 bg-teal text-white rounded-lg text-[13px] font-medium hover:opacity-90 disabled:opacity-50"
          >
            {saveReport.isPending ? 'Wird erstellt…' : 'Report erstellen'}
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-5 gap-3 mb-5">
        {[
          { label: 'CRO-Score', value: analysis.score || '—', color: '#F59E0B' },
          { label: 'Elemente analysiert', value: userAnalytics.length || '—', color: '#0C9488' },
          { label: 'Maßnahmen', value: measures.length || '—', color: '#3B82F6' },
          { label: 'Kritisch', value: measures.filter(m => m.priority === 'high').length || '—', color: '#EF4444' },
          { label: 'Tokens', value: analysis.tokens_used ? `${(analysis.tokens_used / 1000).toFixed(1)}K` : '—', color: '#8B5CF6' },
        ].map((kpi) => (
          <div key={kpi.label} className="bg-white rounded-xl shadow-sm border border-black/[.07] p-4 text-center">
            <div className="text-[26px] font-extrabold" style={{ color: kpi.color }}>{kpi.value}</div>
            <div className="text-[11px] text-gray-500 mt-0.5">{kpi.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-bg rounded-lg p-1 w-fit">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-md text-[13px] transition-colors ${
              tab === t.key ? 'bg-white shadow-sm font-medium' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab: Management Summary */}
      {tab === 'summary' && (
        <div className="space-y-4">
          {narrative.intro && (
            <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-6">
              <h3 className="font-bold text-[16px] mb-3">Einleitung</h3>
              {narrative.intro.split(/\n\n+/).map((para, i) => (
                <p key={i} className="text-[14px] text-gray-700 leading-relaxed mb-3 last:mb-0">{para}</p>
              ))}
            </div>
          )}
          {summary ? (
            <>
              <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-6">
                <h3 className="font-bold text-[16px] mb-3">Management Summary</h3>
                <div className="grid grid-cols-3 gap-4 mb-4 text-[13px]">
                  <div><span className="text-gray-500">Kunde:</span> <span className="font-medium ml-1">{summary.client || '—'}</span></div>
                  <div><span className="text-gray-500">URL:</span> <span className="font-medium ml-1 truncate">{summary.url}</span></div>
                  <div><span className="text-gray-500">Seitentyp:</span> <span className="font-medium ml-1">{summary.page_type}</span></div>
                </div>
                {summary.summary?.split(/\n\n+/).map((para, i) => (
                  <p key={i} className="text-[14px] text-gray-700 leading-relaxed mb-3">{para}</p>
                ))}
                {summary.potential && (
                  <div className="bg-teal-light rounded-lg p-3 text-[13px] mt-2">
                    <span className="font-semibold text-teal">Potenzial:</span> <span className="text-teal">{summary.potential}</span>
                  </div>
                )}
              </div>
              {summary.key_findings?.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-6">
                  <h3 className="font-semibold text-[15px] mb-3">Kernbefunde</h3>
                  <ul className="space-y-2">
                    {summary.key_findings.map((f, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-[13.5px]">
                        <span className="text-teal font-bold mt-0.5">{i + 1}.</span>
                        <span className="text-gray-700">{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : !narrative.intro && (
            <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-8 text-center text-gray-400 text-sm">
              Kein Management Summary verfügbar (Legacy-Analyse oder anderer Provider).
            </div>
          )}
          {narrative.conclusion && (
            <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-6">
              <h3 className="font-bold text-[16px] mb-3">Fazit & Roadmap</h3>
              {narrative.conclusion.split(/\n\n+/).map((para, i) => (
                <p key={i} className="text-[14px] text-gray-700 leading-relaxed mb-3 last:mb-0">{para}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab: Grundlagen / Wahrnehmungs- & Vertrauensmodell (Block 2) */}
      {tab === 'foundations' && (() => {
        const current = foundationsDraft ?? foundations ?? FOUNDATION_TEMPLATE;
        const dirty = foundationsDraft !== null && JSON.stringify(foundationsDraft) !== JSON.stringify(foundations || FOUNDATION_TEMPLATE);
        const setBlock = (idx, patch) => {
          const blocks = current.blocks.map((b, i) => i === idx ? { ...b, ...patch } : b);
          setFoundationsDraft({ ...current, blocks });
        };
        const addBlock = () => setFoundationsDraft({
          ...current,
          blocks: [...current.blocks, { key: `custom_${current.blocks.length + 1}`, title: 'Neuer Baustein', body: '', applies: true }],
        });
        const removeBlock = (idx) => setFoundationsDraft({
          ...current,
          blocks: current.blocks.filter((_, i) => i !== idx),
        });
        const save = async () => {
          try {
            await updateFoundations.mutateAsync({ analysisId: parseInt(id), foundations: current });
            setFoundationsDraft(null);
          } catch (err) { alert('Fehler: ' + (err.response?.data?.message || err.message)); }
        };
        const reset = () => setFoundationsDraft(null);

        return (
          <div className="space-y-4">
            <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-bold text-[16px] mb-1">Block 2 — Grundlagen / Wahrnehmungs- & Vertrauensmodell</h3>
                  <p className="text-[12.5px] text-gray-500">Das WHY — Z-/Gutenberg-/F-Pattern, Vertrauensmechanik, Kognitive Leichtigkeit. Pro Analyse individuell auf die Seite hergeleitet.</p>
                </div>
                {dirty && (
                  <div className="flex gap-2">
                    <button onClick={reset} className="px-3 py-1.5 text-[12px] text-gray-600 hover:text-gray-900">Verwerfen</button>
                    <button onClick={save} disabled={updateFoundations.isPending}
                      className="px-3.5 py-1.5 bg-teal text-white rounded-lg text-[12.5px] font-medium hover:opacity-90 disabled:opacity-50">
                      {updateFoundations.isPending ? 'Speichere…' : 'Speichern'}
                    </button>
                  </div>
                )}
              </div>
              <input
                type="text"
                value={current.headline || ''}
                onChange={(e) => setFoundationsDraft({ ...current, headline: e.target.value })}
                placeholder="Überschrift"
                className="w-full px-3 py-2 text-[15px] font-semibold border border-gray-300 rounded-lg mb-3 focus:outline-none focus:border-teal focus:ring-1 focus:ring-teal"
              />
              <textarea
                value={current.intro || ''}
                onChange={(e) => setFoundationsDraft({ ...current, intro: e.target.value })}
                placeholder="Einleitender Fließtext (3–6 Sätze): Wieso ist Block 2 für DIESE Seite relevant? Was muss der Leser verstanden haben, bevor die Maßnahmen Sinn ergeben?"
                rows={5}
                className="w-full px-3 py-2 text-[13.5px] border border-gray-300 rounded-lg focus:outline-none focus:border-teal focus:ring-1 focus:ring-teal"
              />
            </div>

            {(current.blocks || []).map((b, idx) => {
              const blockAssets = assetsBySection('foundations').filter((a) => (a.caption || '').includes(b.key)) ;
              return (
                <div key={b.key + idx} className={`bg-white rounded-xl shadow-sm border border-black/[.07] p-6 ${!b.applies ? 'opacity-60' : ''}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <input
                      type="text"
                      value={b.title || ''}
                      onChange={(e) => setBlock(idx, { title: e.target.value })}
                      className="flex-1 px-3 py-1.5 text-[14px] font-semibold border border-gray-200 rounded-lg focus:outline-none focus:border-teal"
                    />
                    <label className="flex items-center gap-1.5 text-[12px] text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!b.applies}
                        onChange={(e) => setBlock(idx, { applies: e.target.checked })}
                        className="accent-teal"
                      />
                      gilt für diese Seite
                    </label>
                    <button onClick={() => removeBlock(idx)} className="text-gray-400 hover:text-red-500 text-[14px]" title="Entfernen">✕</button>
                  </div>
                  <textarea
                    value={b.body || ''}
                    onChange={(e) => setBlock(idx, { body: e.target.value })}
                    placeholder={`Inhalt zu „${b.title}" — wie gilt das Pattern hier? Konkrete Anwendung auf diese Seite, mit Begründung. Studien-Bezug optional.`}
                    rows={6}
                    className="w-full px-3 py-2 text-[13px] border border-gray-300 rounded-lg focus:outline-none focus:border-teal focus:ring-1 focus:ring-teal"
                  />
                </div>
              );
            })}
            <div className="flex justify-between items-center">
              <button onClick={addBlock} className="px-3 py-1.5 text-[12.5px] text-teal hover:underline">+ weiteren Baustein hinzufügen</button>
              {dirty && (
                <button onClick={save} disabled={updateFoundations.isPending}
                  className="px-4 py-2 bg-teal text-white rounded-lg text-[13px] font-medium hover:opacity-90 disabled:opacity-50">
                  {updateFoundations.isPending ? 'Speichere…' : 'Block 2 speichern'}
                </button>
              )}
            </div>

            {/* Bilder zur Foundations-Sektion */}
            <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-6">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-[14px]">Bilder zu Block 2</h3>
                  <p className="text-[12px] text-gray-500">Z-Pattern-Skizze, Heatmap, Gutenberg-Diagramm — werden im PDF unter Block 2 ausgegeben.</p>
                </div>
                <label className="px-3 py-1.5 bg-teal text-white rounded-lg text-[12.5px] font-medium hover:opacity-90 cursor-pointer">
                  {uploadAsset.isPending ? 'Lade…' : '+ Bild hochladen'}
                  <input type="file" accept="image/*" className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleSectionUpload(f, 'foundations'); e.target.value = ''; }}
                    disabled={uploadAsset.isPending} />
                </label>
              </div>
              {assetsBySection('foundations').length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {assetsBySection('foundations').map((a) => (
                    <div key={a.id} className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50 relative group">
                      <img src={fileUrl(a.id)} alt={a.caption || ''} className="block w-full h-44 object-contain" />
                      <button onClick={() => handleAssetDelete(a.id)}
                        className="absolute top-1.5 right-1.5 bg-white/90 hover:bg-red-500 hover:text-white text-gray-500 rounded-full w-6 h-6 text-[12px] opacity-0 group-hover:opacity-100 transition">✕</button>
                      {a.caption && <div className="px-2 py-1 text-[11px] text-gray-600 bg-white border-t border-gray-100 truncate">{a.caption}</div>}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-[12px] text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">
                  Noch keine Bilder zu Block 2.
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Tab: Web Analytics */}
      {tab === 'web' && (
        <div className="space-y-4">
          {webAnalytics ? (
            <>
              <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-6">
                <h3 className="font-bold text-[16px] mb-3">Web Analytics</h3>
                <p className="text-[13.5px] text-gray-700 leading-relaxed">{webAnalytics.summary}</p>
              </div>

              {webAnalytics.traffic_sources?.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-6">
                  <h3 className="font-semibold text-[15px] mb-3">Traffic-Quellen</h3>
                  <table className="w-full text-[13px]">
                    <thead>
                      <tr className="border-b border-gray-200 text-left">
                        <th className="py-2.5 text-gray-500 font-medium">Quelle</th>
                        <th className="py-2.5 text-gray-500 font-medium">Sessions</th>
                        <th className="py-2.5 text-gray-500 font-medium">Ø Verweildauer</th>
                        <th className="py-2.5 text-gray-500 font-medium">Conv. Rate</th>
                        <th className="py-2.5 text-gray-500 font-medium">Interpretation</th>
                      </tr>
                    </thead>
                    <tbody>
                      {webAnalytics.traffic_sources.map((s, i) => (
                        <tr key={i} className="border-b border-gray-100 last:border-0">
                          <td className="py-2.5 font-medium">{s.source}</td>
                          <td className="py-2.5">{s.sessions ?? '—'}</td>
                          <td className="py-2.5">{s.avg_duration_sec ? `${s.avg_duration_sec} Sek.` : '—'}</td>
                          <td className="py-2.5">{s.conversion_rate != null ? `${s.conversion_rate}%` : '—'}</td>
                          <td className="py-2.5 text-gray-500 text-[12px]">{s.interpretation}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                {webAnalytics.devices?.length > 0 && (
                  <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
                    <h3 className="font-semibold text-[14px] mb-3">Geräte</h3>
                    <div className="space-y-2.5">
                      {webAnalytics.devices.map((d, i) => (
                        <div key={i}>
                          <div className="flex justify-between text-[12.5px] mb-1">
                            <span className="font-medium">{d.type}</span>
                            <span className="text-gray-500">{d.share_percent}%</span>
                          </div>
                          <Bar pct={d.share_percent} color="#0C9488" />
                          {d.interpretation && <p className="text-[11px] text-gray-400 mt-1">{d.interpretation}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {webAnalytics.screen_resolutions?.length > 0 && (
                  <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
                    <h3 className="font-semibold text-[14px] mb-3">Bildschirmauflösungen</h3>
                    <div className="space-y-2">
                      {webAnalytics.screen_resolutions.map((r, i) => (
                        <div key={i} className="flex justify-between text-[12.5px] py-1.5 border-b border-gray-100 last:border-0">
                          <span className="font-mono text-[11.5px]">{r.resolution}</span>
                          <span className="text-gray-500">{r.sessions} Sessions · {r.avg_duration_sec ? `${r.avg_duration_sec}s` : '—'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {webAnalytics.insights?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                  <h3 className="font-semibold text-[14px] text-amber-800 mb-2">Insights & Ableitungen</h3>
                  <ul className="space-y-1.5">
                    {webAnalytics.insights.map((ins, i) => (
                      <li key={i} className="flex items-start gap-2 text-[13px] text-amber-900">
                        <span className="text-amber-500 mt-0.5">→</span>
                        <span>{ins}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-8 text-center text-gray-400 text-sm">
              Keine Web Analytics Daten verfügbar. Aktiviere das Tracking oder lade GA-Exports hoch.
            </div>
          )}
        </div>
      )}

      {/* Tab: User Analytics */}
      {tab === 'user' && (
        <div className="space-y-4">
          {userAnalytics.length > 0 ? userAnalytics.map((el) => {
            const pr = PRIORITY_MAP[el.priority] || PRIORITY_MAP.medium;
            return (
              <div key={el.element_nr} className="bg-white rounded-xl shadow-sm border border-black/[.07] overflow-hidden">
                <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 bg-gray-50/50">
                  <div className="w-8 h-8 rounded-full bg-sidebar text-white flex items-center justify-center text-[13px] font-bold">{el.element_nr}</div>
                  <h3 className="font-semibold text-[15px] flex-1">{el.element_name}</h3>
                  <Badge bg={pr.bg} color={pr.color}>{pr.icon} {pr.label}</Badge>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <h4 className="text-[12px] text-gray-500 font-semibold uppercase tracking-wide mb-1.5">Was man sieht</h4>
                    <p className="text-[13.5px] text-gray-700 leading-relaxed">{el.what_you_see}</p>
                  </div>
                  <div className="bg-teal-light rounded-lg p-4">
                    <h4 className="text-[12px] text-teal font-semibold uppercase tracking-wide mb-1.5">Optimierung</h4>
                    <p className="text-[13.5px] text-gray-700 leading-relaxed">{el.optimization}</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="text-[12px] text-blue-600 font-semibold uppercase tracking-wide mb-1.5">Biases & Effekte</h4>
                    <p className="text-[13.5px] text-gray-700 leading-relaxed">{el.biases_and_effects}</p>
                  </div>
                </div>
              </div>
            );
          }) : (
            <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-8 text-center text-gray-400 text-sm">
              Keine User Analytics verfügbar.
            </div>
          )}
        </div>
      )}

      {/* Tab: Maßnahmen */}
      {tab === 'measures' && (
        <div className="bg-white rounded-xl shadow-sm border border-black/[.07]">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h3 className="font-bold text-[16px]">Maßnahmenübersicht</h3>
              <p className="text-[12.5px] text-gray-500 mt-0.5">
                {measures.length} KI-Maßnahmen
                {manualMeasures.length > 0 && <> · {manualMeasures.length} eigene</>}
                {' '}— priorisiert
              </p>
            </div>
            <button
              onClick={startNewMeasure}
              className="px-3.5 py-2 bg-teal text-white rounded-lg text-[12.5px] font-medium hover:opacity-90"
            >
              {manualForm !== null ? '× Schließen' : '+ Eigene Maßnahme'}
            </button>
          </div>
          {manualForm !== null && (
            <form onSubmit={submitForm} className="border-b border-gray-200 px-6 py-5 bg-amber-50/40 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-[14px]">{manualForm === 'new' ? 'Neue eigene Maßnahme' : `Maßnahme M${manualForm} bearbeiten`}</h4>
                <button type="button" onClick={cancelForm} className="text-[12px] text-gray-500 hover:text-gray-700">Abbrechen</button>
              </div>
              <input
                type="text"
                placeholder="Maßnahme (Pflicht) — z.B. 'Trust-Logos in den Header verschieben'"
                value={formData.measure || ''}
                onChange={(e) => setFormData({ ...formData, measure: e.target.value })}
                required
                maxLength={1000}
                autoFocus
                className="w-full px-3 py-2 text-[13px] border border-gray-300 rounded-lg focus:outline-none focus:border-teal focus:ring-1 focus:ring-teal"
              />
              <textarea
                placeholder="Ziel / Effekt — was soll dadurch erreicht werden?"
                value={formData.goal_effect || ''}
                onChange={(e) => setFormData({ ...formData, goal_effect: e.target.value })}
                rows={2}
                maxLength={2000}
                className="w-full px-3 py-2 text-[13px] border border-gray-300 rounded-lg focus:outline-none focus:border-teal focus:ring-1 focus:ring-teal"
              />
              <textarea
                placeholder="Details / Test-Plan / Notizen (optional)"
                value={formData.details || ''}
                onChange={(e) => setFormData({ ...formData, details: e.target.value })}
                rows={3}
                maxLength={4000}
                className="w-full px-3 py-2 text-[13px] border border-gray-300 rounded-lg focus:outline-none focus:border-teal focus:ring-1 focus:ring-teal"
              />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-[11px] text-gray-500 mb-1">Priorität</label>
                  <select
                    value={formData.priority || 'medium'}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    className="w-full px-2 py-1.5 text-[12.5px] border border-gray-300 rounded-lg"
                  >
                    <option value="high">Hoch</option>
                    <option value="medium">Mittel</option>
                    <option value="low">Gering</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] text-gray-500 mb-1">Aufwand</label>
                  <select
                    value={formData.effort || 'medium'}
                    onChange={(e) => setFormData({ ...formData, effort: e.target.value })}
                    className="w-full px-2 py-1.5 text-[12.5px] border border-gray-300 rounded-lg"
                  >
                    <option value="low">Niedrig</option>
                    <option value="medium">Mittel</option>
                    <option value="high">Hoch</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] text-gray-500 mb-1">Impact</label>
                  <input
                    type="text"
                    placeholder="z.B. +5-8%"
                    value={formData.conv_impact || ''}
                    onChange={(e) => setFormData({ ...formData, conv_impact: e.target.value })}
                    maxLength={50}
                    className="w-full px-2 py-1.5 text-[12.5px] border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-500 mb-1">Kategorie</label>
                  <select
                    value={formData.category || ''}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-2 py-1.5 text-[12.5px] border border-gray-300 rounded-lg"
                  >
                    <option value="">— keine —</option>
                    {Object.entries(CATEGORY_MAP).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[11px] text-gray-500 mb-1">Element-Bezug (optional)</label>
                <input
                  type="text"
                  placeholder="z.B. Hauptheadline, CTA oben rechts, übergeordnet"
                  value={formData.element_ref || ''}
                  onChange={(e) => setFormData({ ...formData, element_ref: e.target.value })}
                  maxLength={200}
                  className="w-full px-3 py-2 text-[12.5px] border border-gray-300 rounded-lg"
                />
              </div>

              <details className="border border-gray-200 rounded-lg bg-white open:bg-amber-50/40">
                <summary className="cursor-pointer px-3 py-2 text-[12.5px] font-semibold text-gray-700 select-none">
                  ergoflix-Detail-Block (Problem · Hypothese · Bezug · Umsetzung · KPI · Risiko)
                  <span className="text-gray-400 font-normal"> — für Top-5-Maßnahmen</span>
                </summary>
                <div className="p-3 pt-1 space-y-2.5">
                  <textarea
                    placeholder="Problem — was ist konkret das Problem auf der Seite? (3–6 Sätze, Daten/Beobachtung)"
                    value={formData.problem || ''}
                    onChange={(e) => setFormData({ ...formData, problem: e.target.value })}
                    rows={3} maxLength={4000}
                    className="w-full px-3 py-2 text-[13px] border border-gray-300 rounded-lg focus:outline-none focus:border-teal focus:ring-1 focus:ring-teal" />
                  <textarea
                    placeholder="Hypothese — wenn wir X tun, erwarten wir Y, weil Z. Konkret und prüfbar."
                    value={formData.hypothesis || ''}
                    onChange={(e) => setFormData({ ...formData, hypothesis: e.target.value })}
                    rows={3} maxLength={4000}
                    className="w-full px-3 py-2 text-[13px] border border-gray-300 rounded-lg focus:outline-none focus:border-teal focus:ring-1 focus:ring-teal" />
                  <textarea
                    placeholder="Bezug auf Block 2 — welcher Wahrnehmungs-/Vertrauens-Mechanismus aus den Grundlagen erklärt das? (z.B. 'Z-Pattern bricht beim Hero, weil…')"
                    value={formData.foundation_ref || ''}
                    onChange={(e) => setFormData({ ...formData, foundation_ref: e.target.value })}
                    rows={2} maxLength={1000}
                    className="w-full px-3 py-2 text-[13px] border border-gray-300 rounded-lg focus:outline-none focus:border-teal focus:ring-1 focus:ring-teal" />
                  <textarea
                    placeholder="Umsetzung — konkrete Schritte, Beispiel-Copy, Komponenten, Reihenfolge. Detailliert genug, dass Designer/Entwickler direkt loslaufen können."
                    value={formData.implementation || ''}
                    onChange={(e) => setFormData({ ...formData, implementation: e.target.value })}
                    rows={5} maxLength={6000}
                    className="w-full px-3 py-2 text-[13px] border border-gray-300 rounded-lg focus:outline-none focus:border-teal focus:ring-1 focus:ring-teal" />
                  <input
                    type="text"
                    placeholder="KPI — Messgröße + Zielwert (z.B. 'CTR Hero-CTA von 1,8 % auf 3,2 % in 30 Tagen')"
                    value={formData.kpi || ''}
                    onChange={(e) => setFormData({ ...formData, kpi: e.target.value })}
                    maxLength={1000}
                    className="w-full px-3 py-2 text-[13px] border border-gray-300 rounded-lg focus:outline-none focus:border-teal focus:ring-1 focus:ring-teal" />
                  <textarea
                    placeholder="Risiko — was kann schiefgehen? (z.B. Mobile-Brüche, Brand-Konflikt, Implementierungs-Aufwand)"
                    value={formData.risk || ''}
                    onChange={(e) => setFormData({ ...formData, risk: e.target.value })}
                    rows={2} maxLength={2000}
                    className="w-full px-3 py-2 text-[13px] border border-gray-300 rounded-lg focus:outline-none focus:border-teal focus:ring-1 focus:ring-teal" />
                </div>
              </details>

              <div className="flex items-center gap-2 pt-1">
                <button
                  type="submit"
                  disabled={createManual.isPending || updateManual.isPending || !formData.measure?.trim()}
                  className="px-4 py-2 bg-teal text-white rounded-lg text-[13px] font-medium hover:opacity-90 disabled:opacity-40"
                >
                  {(createManual.isPending || updateManual.isPending) ? 'Speichere…' : (manualForm === 'new' ? 'Anlegen' : 'Speichern')}
                </button>
                <button
                  type="button"
                  onClick={cancelForm}
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-[13px] font-medium hover:bg-gray-50"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          )}
          {/* Top-5 Detail-Block (priorisierte Maßnahmen, ergoflix-Stil) */}
          {topMeasures.length > 0 && (
            <div className="border-b border-gray-200 bg-amber-50/30 px-6 py-5 space-y-5">
              <div>
                <h4 className="font-bold text-[15px]">★ Detail-Verschriftlichung der Top {topMeasures.length} Maßnahme{topMeasures.length > 1 ? 'n' : ''}</h4>
                <p className="text-[12px] text-gray-600 mt-0.5">Story-getrieben, mit Bezug auf Block 2 — die priorisierten Maßnahmen, die der Kunde wirklich verstehen soll.</p>
              </div>
              {topMeasures.map((m) => {
                const measureAssets = assetsBySection('measure', m.nr);
                return (
                  <div key={`top-${m.nr}`} className="bg-white rounded-xl border border-amber-200 shadow-sm p-5">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div>
                        <div className="text-[11px] text-amber-600 font-semibold uppercase tracking-wide">Maßnahme M{m.nr}</div>
                        <h5 className="font-bold text-[16px] mt-0.5">{m.measure}</h5>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => startEditMeasure(m)} className="px-2.5 py-1 text-[11.5px] bg-white border border-gray-300 rounded hover:bg-gray-50">Bearbeiten</button>
                        <button onClick={() => toggleTopMeasure(m.nr)} className="px-2.5 py-1 text-[11.5px] bg-white border border-gray-300 rounded hover:bg-red-50 hover:border-red-200 hover:text-red-600">Aus Top entfernen</button>
                      </div>
                    </div>
                    {m.goal_effect && <p className="text-[13px] text-gray-700 italic mb-3">{m.goal_effect}</p>}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[13px]">
                      {m.problem && (
                        <div className="bg-red-50 rounded-lg p-3">
                          <div className="text-[10.5px] text-red-600 font-semibold uppercase tracking-wide mb-1">Problem</div>
                          <p className="text-gray-800 leading-relaxed whitespace-pre-line">{m.problem}</p>
                        </div>
                      )}
                      {m.hypothesis && (
                        <div className="bg-blue-50 rounded-lg p-3">
                          <div className="text-[10.5px] text-blue-700 font-semibold uppercase tracking-wide mb-1">Hypothese</div>
                          <p className="text-gray-800 leading-relaxed whitespace-pre-line">{m.hypothesis}</p>
                        </div>
                      )}
                      {m.foundation_ref && (
                        <div className="bg-purple-50 rounded-lg p-3 md:col-span-2">
                          <div className="text-[10.5px] text-purple-700 font-semibold uppercase tracking-wide mb-1">Bezug auf Block 2 (Wahrnehmung & Vertrauen)</div>
                          <p className="text-gray-800 leading-relaxed whitespace-pre-line">{m.foundation_ref}</p>
                        </div>
                      )}
                      {m.implementation && (
                        <div className="bg-teal-light rounded-lg p-3 md:col-span-2">
                          <div className="text-[10.5px] text-teal font-semibold uppercase tracking-wide mb-1">Umsetzung</div>
                          <p className="text-gray-800 leading-relaxed whitespace-pre-line">{m.implementation}</p>
                        </div>
                      )}
                      {m.kpi && (
                        <div className="bg-emerald-50 rounded-lg p-3">
                          <div className="text-[10.5px] text-emerald-700 font-semibold uppercase tracking-wide mb-1">KPI</div>
                          <p className="text-gray-800 leading-relaxed whitespace-pre-line">{m.kpi}</p>
                        </div>
                      )}
                      {m.risk && (
                        <div className="bg-amber-50 rounded-lg p-3">
                          <div className="text-[10.5px] text-amber-700 font-semibold uppercase tracking-wide mb-1">Risiko</div>
                          <p className="text-gray-800 leading-relaxed whitespace-pre-line">{m.risk}</p>
                        </div>
                      )}
                    </div>

                    {/* Bilder zur Maßnahme */}
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[11.5px] font-semibold text-gray-600 uppercase tracking-wide">Bilder zu M{m.nr}</span>
                        <label className="text-[11.5px] text-teal hover:underline cursor-pointer">
                          + Bild hochladen
                          <input type="file" accept="image/*" className="hidden"
                            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleSectionUpload(f, 'measure', m.nr); e.target.value = ''; }}
                            disabled={uploadAsset.isPending} />
                        </label>
                      </div>
                      {measureAssets.length > 0 ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
                          {measureAssets.map((a) => (
                            <div key={a.id} className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50 relative group">
                              <img src={fileUrl(a.id)} alt={a.caption || ''} className="block w-full h-32 object-contain" />
                              <button onClick={() => handleAssetDelete(a.id)}
                                className="absolute top-1 right-1 bg-white/90 hover:bg-red-500 hover:text-white text-gray-500 rounded-full w-5 h-5 text-[10px] opacity-0 group-hover:opacity-100 transition">✕</button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-[11px] text-gray-400">Noch keine Bilder zu dieser Maßnahme.</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {(measures.length > 0 || manualMeasures.length > 0) ? (
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-gray-200 text-left">
                  <th className="px-6 py-3 text-gray-500 font-medium w-8">#</th>
                  <th className="px-6 py-3 text-gray-500 font-medium">Maßnahme</th>
                  <th className="px-6 py-3 text-gray-500 font-medium">Ziel / Effekt</th>
                  <th className="px-6 py-3 text-gray-500 font-medium w-24">Priorität</th>
                  <th className="px-6 py-3 text-gray-500 font-medium w-24">Aufwand</th>
                  <th className="px-6 py-3 text-gray-500 font-medium w-24">Impact</th>
                  <th className="px-6 py-3 w-12 text-center text-gray-500 font-medium" title="Top-5 Detail-Block">★</th>
                  <th className="px-6 py-3 w-20"></th>
                </tr>
              </thead>
              <tbody>
                {measures.map((m) => {
                  const pr = PRIORITY_MAP[m.priority] || PRIORITY_MAP.medium;
                  const effortMap = { low: 'Niedrig', medium: 'Mittel', high: 'Hoch' };
                  const isOpen = openMeasure === m.nr;
                  const hasDetails = !!m.details;
                  return (
                    <Fragment key={m.nr}>
                      <tr
                        className={`border-b border-gray-100 last:border-0 hover:bg-gray-50/50 ${hasDetails ? 'cursor-pointer' : ''}`}
                        onClick={() => hasDetails && setOpenMeasure(isOpen ? null : m.nr)}
                      >
                        <td className="px-6 py-3.5 font-bold text-gray-400 align-top">{m.nr}</td>
                        <td className="px-6 py-3.5 align-top">
                          <div className="font-medium">{m.measure}</div>
                          {(m.category || m.element_ref) && (
                            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                              {m.category && (() => {
                                const c = CATEGORY_MAP[m.category] || CATEGORY_MAP.other;
                                return (
                                  <span
                                    className="inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-medium uppercase tracking-wide"
                                    style={{ background: c.bg, color: c.color }}
                                  >
                                    {c.label}
                                  </span>
                                );
                              })()}
                              {m.element_ref && m.element_ref !== 'übergeordnet' && (
                                <span className="text-[11px] text-gray-500">
                                  → {m.element_ref}
                                </span>
                              )}
                              {m.element_ref === 'übergeordnet' && (
                                <span className="text-[11px] text-gray-400 italic">
                                  übergeordnet
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-3.5 text-gray-600 text-[12.5px] align-top">{m.goal_effect}</td>
                        <td className="px-6 py-3.5 align-top"><Badge bg={pr.bg} color={pr.color}>{pr.icon} {pr.label}</Badge></td>
                        <td className="px-6 py-3.5 text-[12.5px] align-top">{effortMap[m.effort] || m.effort}</td>
                        <td className="px-6 py-3.5 font-medium text-teal align-top">{m.conv_impact}</td>
                        <td className="px-6 py-3.5 text-center align-top">
                          <button
                            onClick={(e) => { e.stopPropagation(); promoteAiToTop(m); }}
                            disabled={topNrs.length >= 5}
                            className={`text-[16px] hover:scale-110 transition ${topNrs.length >= 5 ? 'text-gray-200 cursor-not-allowed' : 'text-gray-300 hover:text-amber-500'}`}
                            title={topNrs.length >= 5 ? 'Top-5 voll — erst eine entfernen' : 'Als Top-Detail ausschreiben (kopiert in eigene Maßnahmen, öffnet Editor)'}
                          >☆</button>
                        </td>
                        <td className="px-6 py-3.5 text-gray-400 text-center align-top">{hasDetails ? (isOpen ? '−' : '+') : ''}</td>
                      </tr>
                      {isOpen && hasDetails && (
                        <tr className="bg-gray-50/70 border-b border-gray-100">
                          <td></td>
                          <td colSpan={7} className="px-6 py-4">
                            <div className="text-[11px] text-gray-500 font-semibold uppercase tracking-wide mb-2">Tiefe Begründung & Test-Plan</div>
                            {m.details.split(/\n\n+/).map((para, i) => (
                              <p key={i} className="text-[13px] text-gray-700 leading-relaxed mb-2 last:mb-0">{para}</p>
                            ))}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
                {manualMeasures.map((m) => {
                  const pr = PRIORITY_MAP[m.priority] || PRIORITY_MAP.medium;
                  const effortMap = { low: 'Niedrig', medium: 'Mittel', high: 'Hoch' };
                  const isOpen = openMeasure === `m-${m.nr}`;
                  const hasDetails = !!m.details;
                  return (
                    <Fragment key={`manual-${m.nr}`}>
                      <tr className="border-b border-gray-100 last:border-0 hover:bg-gray-50/50 group">
                        <td className="px-6 py-3.5 font-bold text-gray-400 align-top">M{m.nr}</td>
                        <td
                          className={`px-6 py-3.5 align-top ${hasDetails ? 'cursor-pointer' : ''}`}
                          onClick={() => hasDetails && setOpenMeasure(isOpen ? null : `m-${m.nr}`)}
                        >
                          <div className="font-medium">{m.measure}</div>
                          {(m.category || m.element_ref) && (
                            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                              {m.category && (() => {
                                const c = CATEGORY_MAP[m.category] || CATEGORY_MAP.other;
                                return (
                                  <span
                                    className="inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-medium uppercase tracking-wide"
                                    style={{ background: c.bg, color: c.color }}
                                  >
                                    {c.label}
                                  </span>
                                );
                              })()}
                              {m.element_ref && m.element_ref !== 'übergeordnet' && (
                                <span className="text-[11px] text-gray-500">→ {m.element_ref}</span>
                              )}
                              {m.element_ref === 'übergeordnet' && (
                                <span className="text-[11px] text-gray-400 italic">übergeordnet</span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-3.5 text-gray-600 text-[12.5px] align-top">{m.goal_effect}</td>
                        <td className="px-6 py-3.5 align-top"><Badge bg={pr.bg} color={pr.color}>{pr.icon} {pr.label}</Badge></td>
                        <td className="px-6 py-3.5 text-[12.5px] align-top">{effortMap[m.effort] || m.effort || '—'}</td>
                        <td className="px-6 py-3.5 font-medium text-teal align-top">{m.conv_impact || '—'}</td>
                        <td className="px-6 py-3.5 text-center align-top">
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleTopMeasure(m.nr); }}
                            className={`text-[16px] hover:scale-110 transition ${topNrs.includes(m.nr) ? 'text-amber-500' : 'text-gray-300 hover:text-amber-500'}`}
                            title={topNrs.includes(m.nr) ? 'Aus Top-Detail entfernen' : 'Als Top-Detail markieren (max. 5)'}
                          >{topNrs.includes(m.nr) ? '★' : '☆'}</button>
                        </td>
                        <td className="px-6 py-3.5 align-top">
                          <div className="flex items-center gap-1.5 justify-end text-[12px]">
                            <button
                              onClick={(e) => { e.stopPropagation(); startEditMeasure(m); }}
                              className="text-gray-500 hover:text-teal"
                              title="Bearbeiten"
                            >
                              ✎
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDeleteManual(m.nr); }}
                              className="text-gray-400 hover:text-red-500"
                              title="Löschen"
                              disabled={deleteManual.isPending}
                            >
                              ✕
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isOpen && hasDetails && (
                        <tr className="bg-gray-50/70 border-b border-gray-100">
                          <td></td>
                          <td colSpan={7} className="px-6 py-4">
                            <div className="text-[11px] text-gray-500 font-semibold uppercase tracking-wide mb-2">Notizen / Details</div>
                            {m.details.split(/\n\n+/).map((para, i) => (
                              <p key={i} className="text-[13px] text-gray-700 leading-relaxed mb-2 last:mb-0">{para}</p>
                            ))}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="py-8 text-center text-gray-400 text-sm">Keine Maßnahmen verfügbar.</div>
          )}
        </div>
      )}

      {/* Tab: Screenshots (Live + VISION) */}
      {tab === 'wireframe' && (
        <div className="space-y-4">
          {/* XD/Figma-Link */}
          <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-6">
            <h3 className="font-bold text-[16px] mb-1">Externer Wireframe-Link (optional)</h3>
            <p className="text-[12.5px] text-gray-500 mb-3">View-URL zum Adobe-XD- oder Figma-Prototyp dieser Analyse — wird im PDF als Referenz verlinkt.</p>
            {(() => {
              const current = wireframeUrlDraft !== null ? wireframeUrlDraft : (analysis.wireframe_url || '');
              const saved = analysis.wireframe_url || '';
              const dirty = wireframeUrlDraft !== null && wireframeUrlDraft !== saved;
              const save = async () => {
                try {
                  await updateWireframeUrl.mutateAsync({
                    analysisId: parseInt(id),
                    wireframe_url: current.trim() || null,
                  });
                  setWireframeUrlDraft(null);
                } catch (err) {
                  alert('Fehler: ' + (err.response?.data?.message || err.message));
                }
              };
              return (
                <>
                  <div className="flex items-center gap-2">
                    <input
                      type="url"
                      placeholder="https://xd.adobe.com/view/... oder https://www.figma.com/file/..."
                      value={current}
                      onChange={(e) => setWireframeUrlDraft(e.target.value)}
                      onBlur={() => { if (dirty) save(); }}
                      className="flex-1 px-3 py-2 text-[13px] border border-gray-300 rounded-lg focus:outline-none focus:border-teal focus:ring-1 focus:ring-teal"
                    />
                    {saved && !dirty && (
                      <a
                        href={saved}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-teal text-white rounded-lg text-[13px] font-medium hover:opacity-90 whitespace-nowrap"
                      >
                        Öffnen
                      </a>
                    )}
                    {dirty && (
                      <button
                        onClick={save}
                        disabled={updateWireframeUrl.isPending}
                        className="px-4 py-2 bg-teal text-white rounded-lg text-[13px] font-medium hover:opacity-90 disabled:opacity-50 whitespace-nowrap"
                      >
                        {updateWireframeUrl.isPending ? 'Speichere…' : 'Speichern'}
                      </button>
                    )}
                  </div>
                  {dirty && (
                    <p className="text-[11px] text-amber-600 mt-1.5">Ungespeicherte Änderung — wird beim Verlassen des Felds automatisch gespeichert.</p>
                  )}
                </>
              );
            })()}
          </div>

          <ScreenshotZone
            title="Live-Seite (analysiertes Original)"
            description="Screenshots der aktuell live geschalteten Seite — Auto-Capture oder manuell. Diese Bilder fließen in die Vision-Analyse als Ist-Zustand ein und werden im PDF-Block 'Aktueller Stand' ausgewiesen."
            accent="teal"
            assets={assetsBySection('page')}
            fileUrl={fileUrl}
            busy={uploadAsset.isPending}
            onUpload={async (file, label) => {
              try {
                await uploadAsset.mutateAsync({
                  analysisId: parseInt(id), file, type: 'screenshot',
                  section: 'page', caption: label,
                });
              } catch (err) {
                alert('Upload fehlgeschlagen: ' + (err.response?.data?.message || err.message));
              }
            }}
            onDelete={async (assetId) => {
              if (!confirm('Screenshot wirklich löschen?')) return;
              try { await deleteAsset.mutateAsync({ analysisId: parseInt(id), assetId }); }
              catch (err) { alert('Fehler: ' + (err.response?.data?.message || err.message)); }
            }}
            onUpdateLabel={async (assetId, caption) => {
              try { await updateAsset.mutateAsync({ analysisId: parseInt(id), assetId, payload: { caption } }); }
              catch (err) { alert('Fehler: ' + (err.response?.data?.message || err.message)); }
            }}
          />

          <ScreenshotZone
            title="VISION — XD-Entwurf"
            description="Wireframes oder Mockups aus Adobe XD / Figma. Diese werden vom Vision-LLM als geplanter Soll-Zustand bewertet (separat vom Live-Stand) und im PDF-Anhang 'VISION' ausgewiesen."
            accent="amber"
            assets={assetsBySection('wireframe')}
            fileUrl={fileUrl}
            busy={uploadAsset.isPending}
            onUpload={async (file, label) => {
              try {
                await uploadAsset.mutateAsync({
                  analysisId: parseInt(id), file, type: 'screenshot',
                  section: 'wireframe', caption: label,
                });
              } catch (err) {
                alert('Upload fehlgeschlagen: ' + (err.response?.data?.message || err.message));
              }
            }}
            onDelete={async (assetId) => {
              if (!confirm('Wireframe wirklich löschen?')) return;
              try { await deleteAsset.mutateAsync({ analysisId: parseInt(id), assetId }); }
              catch (err) { alert('Fehler: ' + (err.response?.data?.message || err.message)); }
            }}
            onUpdateLabel={async (assetId, caption) => {
              try { await updateAsset.mutateAsync({ analysisId: parseInt(id), assetId, payload: { caption } }); }
              catch (err) { alert('Fehler: ' + (err.response?.data?.message || err.message)); }
            }}
          />

          {wireframes.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-6">
              <h3 className="font-bold text-[16px] mb-1">Wireframe-Vorschläge der KI</h3>
              <p className="text-[13px] text-gray-500 mb-4">Konkrete Layout-Änderungen aus der Analyse — werden im PDF im VISION-Block ausgewiesen.</p>
              <div className="space-y-3">
                {wireframes.map((w, i) => (
                  <div key={i} className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-[14.5px] mb-3">{w.area}</h4>
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div className="bg-red-50 rounded-lg p-4">
                        <div className="text-[11px] text-red-500 font-semibold uppercase tracking-wide mb-1">Ist-Zustand</div>
                        <p className="text-[13px] text-gray-700">{w.current}</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4">
                        <div className="text-[11px] text-green-600 font-semibold uppercase tracking-wide mb-1">Soll-Zustand</div>
                        <p className="text-[13px] text-gray-700">{w.proposed}</p>
                      </div>
                    </div>
                    <p className="text-[12.5px] text-gray-500"><span className="font-medium">Begründung:</span> {w.reasoning}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab: Faktoren (Legacy) */}
      {tab === 'factors' && (
        <div className="bg-white rounded-xl shadow-sm border border-black/[.07]">
          {factors.map((f, i) => (
            <div key={i} className="px-5 py-3.5 border-b border-gray-100 last:border-0">
              <div className="flex items-center gap-2 mb-1">
                <Badge
                  bg={f.status === 'pass' ? '#D1FAE5' : f.status === 'warn' ? '#FEF3C7' : '#FEE2E2'}
                  color={f.status === 'pass' ? '#10B981' : f.status === 'warn' ? '#F59E0B' : '#EF4444'}
                >
                  {f.status}
                </Badge>
                <span className="text-[13px] font-medium">{f.id}</span>
                {f.score != null && <span className="text-[12px] text-gray-400 ml-auto">{f.score}/100</span>}
              </div>
              {f.problem && <p className="text-[12.5px] text-gray-600">{f.problem}</p>}
              {f.recommendation && <p className="text-[12.5px] text-teal mt-1">{f.recommendation}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
